import * as React from "react";
import { envCplnReferenceOptions } from "../../../../schema/types/workload/entity";
import { SecretTypeTag } from "../../../../components/antd/kindselect/tagRenderers/secretTypeTag";
import { useDrawerContext } from "../../../../components/antd/drawerContext";
import { secretTypeActionMap } from "../utils/utils";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../../gvc/testNewForm/utils";
import { CpInput } from "../../../gvc/testNewForm/CpInput";
import { CpTextArea } from "../../../gvc/testNewForm/CpTextArea";
import { CpSelect } from "../../../gvc/testNewForm/CpSelect";
import { CpAutoComplete } from "../../../gvc/testNewForm/CpAutoComplete";
import { store } from "../../../../store";
import { useAppSelector } from "../../../../store/hooks";
import { selectOrg } from "../../../../store/contextSlice";
import { cloneDeep, get } from "lodash";
import { EnvVarInput } from "../../../../schema/types/workload/input";
import { Button, Input, Tooltip } from "antd";
import { LinkOutlined, MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { parseLink } from "../../../../utils/linkParser/linkParser";
import { useCpFormContext } from "../../../../components/form/useCpFormContext";
import { KindSelectState } from "../../../../components/antd/kindselect/KindSelect";
import { CpKindSelect } from "../../../gvc/testNewForm/temp/CpKindSelect";
import { EmbeddedSecretCreate } from "../../../secret/create/embedded";
import { RevealState } from "../../../../reactHooks/useRevealEnvSecrets";

interface Props {
  containerIndex: number;
  index: number;
  revealState?: RevealState;
}
export const EnvVarItem: React.FC<Props> = React.memo(({ containerIndex, index, revealState }) => {
  const { id } = useCpFormContext();
  const drawer = useDrawerContext();
  const org = useAppSelector(selectOrg);

  const envVarId = useCpFormValue(`containers.${containerIndex}.env.${index}.id`);
  const prefix = useCpFormValue(`containers.${containerIndex}.env.${index}.prefix`);
  const secret = useCpFormValue(`containers.${containerIndex}.env.${index}.cplnSecret`);
  const secretCache = useCpFormValue(`meta.secretCache`);

  function remove() {
    const form = store.getState().form.instances[id];
    const items = cloneDeep(get(form.values, `containers.${containerIndex}.env`)) as EnvVarInput[];
    items.splice(index, 1);
    formSetValue(id, `containers.${containerIndex}.env`, items);

    formTriggerValidation(id);
  }

  let secretPropertySuggestions: string[] = React.useMemo(() => {
    let secretPropertySuggestions: string[] = [];
    if (prefix === "cpln://secret/" && !!secret) {
      const secretName = secret.value;
      const cachedSecret = secretCache[secretName];
      if (cachedSecret) {
        const actionMap = secretTypeActionMap[cachedSecret.type];
        if (actionMap.supportsProperties) {
          secretPropertySuggestions = actionMap.properties;
        }
      }
    }
    return secretPropertySuggestions;
  }, [secretCache, secret, prefix]);

  const secretLink =
    prefix === "cpln://secret/" && secret.value ? `/console/org/${org}/secret/${secret.value}` : null;

  const isSecretRevealed = revealState?.isRevealed && prefix === "cpln://secret/" && secret.value;

  return (
    <>
      <CpInput name={`containers.${containerIndex}.env.${index}.name`} className={"col-span-3"} style={{ alignSelf: "start" }} />
      <CpSelect
        name={`containers.${containerIndex}.env.${index}.prefix`}
        options={[
          { label: "Literal Value", value: "" },
          { label: "CPLN Secret", value: "cpln://secret/" },
          { label: "K8s Secret", value: "k8s://secret/" },
          { label: "Built-in", value: "cpln://reference/" },
        ]}
        optionRender={(option) => (
          <div className="flex flex-col gap-1">
            <span>{option.label}</span>
            {option.value ? <span className="text-sm">{option.value}</span> : null}
          </div>
        )}
        className={"col-span-1"}
        style={{ minWidth: 160, alignSelf: "start" }}
      />
      <div className="flex items-start gap-2 w-full col-span-4">
        {prefix === "cpln://reference/" ? (
          <CpSelect
            name={`containers.${containerIndex}.env.${index}.reference`}
            options={envCplnReferenceOptions.map((v: string) => ({ label: v, value: v }))}
            style={{ minWidth: 140 }}
            className={"flex-grow"}
          />
        ) : null}
        {prefix !== "cpln://reference/" && prefix !== "cpln://secret/" && prefix !== `k8s://secret/` ? (
          <CpTextArea name={`containers.${containerIndex}.env.${index}.text`} className={"flex-grow"} autoSize />
        ) : null}
        {prefix === `k8s://secret/` ? (
          <CpTextArea
            name={`containers.${containerIndex}.env.${index}.k8sSecret`}
            className={"flex-grow"}
            autoSize
          />
        ) : null}
        {isSecretRevealed ? (
          <>
            <Input.TextArea
              readOnly
              value={
                revealState.errors[envVarId]
                  ? revealState.errors[envVarId]
                  : revealState.revealedValues[envVarId] ?? ""
              }
              status={revealState.errors[envVarId] ? "error" : undefined}
              className="flex-grow"
              style={{ maxHeight: 128 }}
              autoSize
            />
            {secretLink ? (
              <Tooltip title="Open Secret">
                <Button type="text" icon={<LinkOutlined />} href={secretLink} target="_blank" />
              </Tooltip>
            ) : null}
          </>
        ) : null}
        {prefix === "cpln://secret/" && !isSecretRevealed ? (
          <>
            <CpKindSelect
              kind="secret"
              name={`containers.${containerIndex}.env.${index}.cplnSecret`}
              tagRenderers={[SecretTypeTag] as any}
              optionValue="name"
              className={"flex-grow"}
            />
            {secret.value ? null : (
              <Tooltip title={`Create Secret`}>
                <Button
                  icon={<PlusOutlined />}
                  onClick={() => {
                    drawer.setPersistent(
                      `workload-container-env-create-secret`,
                      "Create Secret",
                      <>
                        <EmbeddedSecretCreate
                          onCancel={() => drawer.closePersistent()}
                          onDone={(itemLink, item) => {
                            drawer.closePersistent();
                            const { name } = parseLink(itemLink, { useInputCtx: true });
                            const state: KindSelectState = {
                              value: name,
                              link: itemLink,
                              item: item,
                            };
                            formSetValue(id, `containers.${containerIndex}.env.${index}.cplnSecret`, state);
                          }}
                        />
                      </>,
                      600,
                    );
                  }}
                />
              </Tooltip>
            )}
            {secret.value ? (
              <>
                <CpAutoComplete
                  name={`containers.${containerIndex}.env.${index}.suffix`}
                  style={{ minWidth: 140 }}
                  options={secretPropertySuggestions.map((v: string) => ({ label: v, value: v }))}
                  placeholder="Property"
                />
                <Tooltip title="Open Secret">
                  <Button type="text" icon={<LinkOutlined />} href={secretLink!} target="_blank" />
                </Tooltip>
              </>
            ) : null}
          </>
        ) : null}
      </div>
      <Button
        type={`text`}
        danger
        icon={<MinusCircleOutlined />}
        onClick={() => remove()}
        style={{ alignSelf: "start" }}
      />
    </>
  );
});
